/**
 * useTenantConfig — the single tenant-config chokepoint (issue #51, ADR 0001).
 *
 * Consolidates the tenant reads that were scattered across the app:
 *   - branding was fetched independently by AppShell, SignInPage, ApplyPage, and
 *     LandingPage (four call sites, three different fallback strings), and
 *   - member config (`GET /tenant/me`) was a separate fetch in CoursesPage.
 *
 * Contract (mirrors ADR 0001 "Build now" §1):
 *   - Public branding (`GET /tenant`) is fetched ALWAYS, so logged-out surfaces
 *     (landing / sign-in / apply) still resolve their brand. The query key and
 *     cache policy match the old `useTenant` hook, so the former four call sites
 *     now share ONE cached request.
 *   - Member config (`GET /tenant/me`) is fetched ONLY when authenticated — this
 *     preserves the public/authenticated endpoint boundary (AC1): a logged-out
 *     page never probes the member endpoint.
 *   - Public identity is owned only by `GET /tenant`. Member identity fields are
 *     not borrowed as presentation fallbacks.
 *   - `meeting`/`locations` stay `null`/`[]` until the member query resolves (AC4);
 *     consumers keep their own defaults (`["in_person"]`, free-text location).
 *   - Explicit request states let the presentation layer distinguish loading or
 *     failure fallbacks from authoritative null branding fields.
 *
 * A generic `features` object is deliberately excluded — neither endpoint provides
 * one. Add typed feature fields only when a real server contract requires them.
 * `sessionPortalEnabled` (issue #321 Milestone 1) is exactly that: one named
 * boolean the server derives per tenant, not a bag that later flags fall into.
 */
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/contexts/AuthContext";
import { publicApi } from "@/lib/api/client";
import { getTenantMe, type TenantMe } from "@/lib/api/tenant";

/** Public, pre-auth branding from `GET /tenant` (tenant resolved server-side by origin). */
export interface TenantBranding {
  slug: string;
  name: string;
  logo_url: string | null;
  parent_org: string | null;
}

/** Raw public-branding and authenticated-operation state. */
export interface TenantConfig {
  recordingPolicy?: TenantMe["recording_policy"];
  slug: string | null;
  name: string | null;
  logoUrl: string | null;
  parentOrg: string | null;
  /** Meeting-provider config; `null` until the authenticated member query resolves. */
  meeting: TenantMe["meeting"] | null;
  /** Location suggestions; empty until the authenticated member query resolves. */
  locations: string[];
  /**
   * Issue #321 Milestone 1 rollout control. FALSE until the member query
   * resolves, so a slow or failed read hides the portal rather than advertising
   * a route the server will refuse. Not authorization — see `tenant.ts`.
   */
  sessionPortalEnabled: boolean;
  /** Existing-account linking stays hidden until tenant policy resolves. */
  existingWorkspaceAccountLinkingEnabled: boolean;
  /** Whether the tenant has a Classroom journey; FALSE until the member query resolves. */
  classroomReferencesEnabled: boolean;
  /** Public-branding request state distinguishes fallback from authoritative nulls. */
  brandingStatus: "loading" | "resolved" | "error";
  /** A failed background refresh does not invalidate already usable branding. */
  brandingRefreshFailed: boolean;
  /** Member operations are disabled while logged out and independent of branding. */
  memberStatus: "disabled" | "loading" | "resolved" | "error";
}

export function useTenantConfig(): TenantConfig {
  const { isAuthenticated } = useAuth();

  const branding = useQuery({
    queryKey: ["tenant", "branding"],
    queryFn: () => publicApi.get<TenantBranding>("/tenant"),
    staleTime: 10 * 60 * 1000, // 10 min — branding changes are rare
    gcTime: 30 * 60 * 1000,
    retry: 1,
  });

  const member = useQuery({
    queryKey: ["tenant", "me"],
    queryFn: getTenantMe,
    enabled: isAuthenticated, // AC1: never probe the member endpoint while logged out
  });

  // Gate member config on auth at the READ, not just the fetch: `enabled: false`
  // stops the network call, but React Query still serves a cached ["tenant","me"]
  // entry from a prior session. After an auth transition that doesn't clear the
  // cache (e.g. a cross-tab sign-out or a silent session expiry — where
  // onAuthStateChange sets me=null but signOut()'s queryClient.clear() never
  // runs), reading member.data directly would leak the previous member's tenant
  // config onto logged-out surfaces. Consulting it only while authenticated keeps
  // AC1 true for reads as well as fetches.
  const memberData = isAuthenticated ? member.data : undefined;
  const brandingStatus = branding.data
    ? "resolved"
    : branding.isPending
      ? "loading"
      : "error";
  const memberStatus = !isAuthenticated
    ? "disabled"
    : member.isPending
      ? "loading"
      : member.isError
        ? "error"
        : "resolved";

  return {
    // GET /tenant exclusively owns public identity. Member data is operational.
    slug: branding.data?.slug ?? null,
    name: branding.data?.name ?? null,
    // Logo + parent org are branding-only (the member endpoint carries neither).
    logoUrl: branding.data?.logo_url ?? null,
    parentOrg: branding.data?.parent_org ?? null,
    meeting: memberData?.meeting ?? null,
    locations: memberData?.locations ?? [],
    recordingPolicy: memberData?.recording_policy ?? null,
    sessionPortalEnabled: memberData?.session_portal_enabled ?? false,
    existingWorkspaceAccountLinkingEnabled:
      memberData?.existing_workspace_account_linking_enabled ?? false,
    classroomReferencesEnabled: memberData?.classroom_references_enabled ?? false,
    brandingStatus,
    brandingRefreshFailed: Boolean(branding.data && branding.isError),
    memberStatus,
  };
}
